<template>
  <Modal
    v-model:visible="isVisible"
    class="edit-brokerage-quote-modal"
    :dismissable-mask="false"
    :header="header"
    :size="viewUplift ? 'lg' : 'md'"
    maximizable
    @keypress.enter="onNext"
  >
    <div class="flex flex-gap-1 flex-column">
      <div class="edit-quote-modal-carrier-profile">
        <div class="flex flex-column align-items-center justify-content-center">
          <CarrierAvatar
            v-if="internalValue.carrier"
            :carrier="internalValue.carrier"
            size="large"
          />
          <FontAwesomeIcon
            v-else
            class="carrier-icon text-5xl"
            :icon="['fas', 'truck']"
          />
          <h1 v-if="internalValue.carrier" class="carrier-name">
            {{ internalValue.carrier.name }}
            {{
              internalValue.carrier.scac?.length
                ? `(${internalValue.carrier.scac})`
                : ''
            }}
          </h1>
          <span v-else>
            {{
              t('common.noParamSelected', { param: t('brokerages.brokers') })
            }}
          </span>
        </div>

        <div v-if="internalValue.carrier" class="carrier-info-row">
          <KeyValuePair
            :label="t('common.name')"
            :value="internalValue.carrier.name"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.scac')"
            :value="internalValue.carrier.scac"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.mcNumber')"
            :value="internalValue.carrier.mcNumber"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.dotNumber')"
            :value="internalValue.carrier.dotNumber"
            class="w-3"
          />
        </div>
      </div>

      <form
        :aria-label="t('rateShop.quotes')"
        class="edit-quote-modal-form flex flex-column flex-gap-1"
      >
        <div class="edit-quote-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-broker"
            :label="t('brokerages.brokers')"
            :errors="v$.carrier.$errors"
            class="w-6"
          >
            <CarrierQuickSearch
              :id="inputId"
              ref="firstInput"
              :model-value="internalValue.carrier"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="!!internalValue.id"
              :type="CapacityProviderType.BROKER"
              label-key="brokerages.brokers"
              append-to="body"
              @update:model-value="onBrokerSelected"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="edit-quote-modal-form-carrier"
            :label="t('carriers.carriers')"
            class="w-6"
            optional
          >
            <CarrierQuickSearch
              :id="inputId"
              :model-value="brokeredCarrier ?? undefined"
              v-bind="ariaProps"
              append-to="body"
              @update:model-value="
                (val?: Carrier) =>
                  (internalValue.brokeredCarrier = val?.toCarrierRef())
              "
              @change="loadCarrierSettings($event.value)"
              @clear="carrierSettings.data = new CarrierSetting()"
            >
              <template #footer>
                <AddButton
                  :label="t('carriers.carriers')"
                  @click="showAddAdHocBrokeredCarrierModal"
                />
              </template>
            </CarrierQuickSearch>
          </InputWrapper>
        </div>

        <div class="edit-quote-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-requested-pickup-early"
            :label="earlyPickupMessage"
            :errors="v$.pickupEarliest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.pickupEarliest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.pickupTimeZone"
              :step-minute="15"
              :default-time="{ hour: 9, minute: 0 }"
              date-format="mm/dd/yy"
              hour-format="12"
              show-icon
              show-time
              :placeholder="earlyPickupMessage"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-requested-pickup-late"
            :label="latePickupMessage"
            :errors="v$.pickupLatest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.pickupLatest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.pickupTimeZone"
              :step-minute="15"
              date-format="mm/dd/yy"
              default-time="5pm"
              hour-format="12"
              show-icon
              show-time
              :placeholder="latePickupMessage"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-requested-delivery-early"
            :label="earlyDeliveryMessage"
            :errors="v$.deliveryEarliest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.deliveryEarliest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.deliveryTimeZone"
              :step-minute="15"
              :default-time="{ hour: 9, minute: 0 }"
              date-format="mm/dd/yy"
              hour-format="12"
              show-icon
              show-time
              :placeholder="earlyDeliveryMessage"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-requested-delivery-late"
            :label="lateDeliveryMessage"
            :errors="v$.deliveryLatest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.deliveryLatest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.deliveryTimeZone"
              :step-minute="15"
              date-format="mm/dd/yy"
              default-time="5pm"
              hour-format="12"
              show-icon
              show-time
              :placeholder="lateDeliveryMessage"
            />
          </InputWrapper>
        </div>

        <div class="edit-quote-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-mode"
            :label="t('shipments.modes')"
            :errors="v$.mode.$errors"
            class="w-4"
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.mode"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :loading="carrierSettings.loading"
              :options="availableModeOptions"
              :placeholder="t('shipments.modes')"
              append-to="body"
              option-label="label"
              option-value="value"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-service-level"
            :label="t('carriers.serviceLevels')"
            class="w-4"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.serviceType"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :loading="carrierSettings.loading"
              :options="availableServiceLevels"
              :placeholder="t('carriers.serviceLevels')"
              append-to="body"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="edit-quote-modal-form-equipment-types"
            :label="
              t('common.paramTypes', { param: t('referenceData.equipment') })
            "
            class="w-4"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.equipmentType"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :loading="carrierSettings.loading"
              :options="availableEquipmentTypes"
              :placeholder="
                t('common.paramTypes', { param: t('referenceData.equipment') })
              "
              option-label="name"
              data-key="id"
              append-to="body"
              filter
              show-clear
            />
          </InputWrapper>
        </div>

        <div
          v-if="
            selectedBrokerSettings &&
            isEmailTender &&
            internalTenderConfig.configOptions
          "
          class="edit-quote-modal-form-row"
        >
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="edit-quote-modal-form-emails"
            :label="t('shipments.emailsCommaSeparated')"
            class="w-6"
          >
            <InputText
              :id="inputId"
              v-model="internalTenderConfig.configOptions.emails"
              v-bind="ariaProps"
              :placeholder="t('shipments.emailsCommaSeparated')"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="edit-quote-modal-form-notes"
            :label="t('orders.notes')"
            class="w-6"
          >
            <Textarea
              :id="inputId"
              v-model="internalTenderConfig.configOptions.notes"
              v-bind="ariaProps"
              :placeholder="t('orders.notes')"
            />
          </InputWrapper>
        </div>
      </form>

      <EditQuoteChargesSection
        v-model="chargeTableItems"
        v-model:currency-code="internalValue.currencyCode"
        :accessorial-codes="accessorialCodes.data"
        :stops="stops"
        :can-view-uplift="viewUplift"
      >
        <template v-if="!internalValue.id" #freightChargeLabel="{ charge }">
          {{
            charge.isLineHaulCharge()
              ? t('brokerages.targetLinehaul')
              : t('brokerages.targetFuel')
          }}
        </template>
      </EditQuoteChargesSection>
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton mode="primary" :label="t('common.next')" @click="onNext" />
    </template>
  </Modal>

  <AddAdHocBrokeredCarrierModal
    v-model:visible="isAddAdHocBrokeredCarrierModalVisible"
    @ad-hoc-brokered-carrier-added="onAdHocBrokeredCarrierAdded"
  />
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { ValidationRule } from '@vuelidate/core';
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import {
  computed,
  nextTick,
  onMounted,
  provide,
  ref,
  toRef,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  afterDateTime,
  beforeDateTime,
  required,
  serverValidationPlaceholder,
  useToast,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  CapacityProviderType,
  CarrierRef,
  CarrierSetting,
  DropdownOption,
  EquipmentType,
  ErrorResponse,
  Loadable,
  Mode,
  Optional,
  TenderConfig,
  TenderMethod,
} from '@rygen/rygen-ui/model';
import { dedupeArray, notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  BrokerageDates,
  BrokeragePayload,
  Carrier,
  RateQuote,
  RateQuoteAccessorial,
  RateSelectionReason,
  WellKnownService,
} from '@/model';
import {
  BaseEditQuoteChargeTableItem,
  EditQuoteClientChargeTableItem,
  EditQuoteThreeplChargeTableItem,
  fromClientChargeTableItems,
  fromThreeplChargeTableItems,
  toClientChargeTableItems,
  toThreeplChargeTableItems,
} from '@/model/rate/editQuote';
import { useAccessorialCodeStore } from '@/stores/AccessorialCode';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useParentEntityStore } from '@/stores/ParentEntity';

import AddAdHocBrokeredCarrierModal from '@/components/brokerage/AddAdHocBrokeredCarrierModal.vue';
import CarrierQuickSearch from '@/components/carrier/CarrierQuickSearch.vue';
import EditQuoteChargesSection from '@/components/rateShop/editQuote/EditQuoteChargesSection.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

export interface BrokerageInfo extends Loadable<RateQuote> {
  data: RateQuote;
  dates: BrokerageDates;
  isShowingModal: boolean;
  shipmentSpecId: number | undefined;
  rateSelectionReason: RateSelectionReason | undefined;
  error: ErrorResponse | undefined;
}

type MaybeNestedValidationRule =
  | Record<string, ValidationRule>
  | Record<string, Record<string, ValidationRule>>;

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  brokerageInfo: {
    type: Object as PropType<BrokerageInfo>,
    default: () => ({ loading: false }),
  },
  brokerageDates: {
    type: BrokerageDates,
    default: () => undefined,
  },
  isBrokeredFromShipment: {
    type: Boolean,
    default: false,
  },
  viewUplift: {
    type: Boolean,
    default: () => false,
  },
});
const { brokerageInfo, brokerageDates, isBrokeredFromShipment, viewUplift } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'confirm', payload: BrokeragePayload): void;
}>();

const firstInput = useTemplateRef('firstInput');

const { accessorialCodes, loadRequestableAccessorialCodes } =
  useAccessorialCodeStore();
const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const toast = useToast();
const uid = useUid('edit-quote-modal').uid;
const isOptionalBrokerageDatesFeatFlagEnabled = useFlag(
  'optional-brokerage-dates-feat-flag',
);
provide('scope', uid);

const internalValue: Ref<RateQuote> = ref(
  new RateQuote(brokerageInfo.value.data),
);
const internalBrokerageDates: Ref<BrokerageDates> = ref(
  new BrokerageDates(brokerageDates?.value),
);

const earlyPickupMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledEarlyPickup')
    : t('orders.requestEarlyPickup');
});

const latePickupMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledLatePickup')
    : t('orders.requestLatePickup');
});

const earlyDeliveryMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledEarlyDelivery')
    : t('orders.requestEarlyDelivery');
});

const lateDeliveryMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledLateDelivery')
    : t('orders.requestLateDelivery');
});

const rules = computed(() => {
  const baseRules: Record<string, MaybeNestedValidationRule> = {
    carrier: {
      required: required(t('brokerages.brokers')),
    },
    mode: {
      required: required(t('shipments.modes')),
    },
    pickupEarliest: {
      required: required(earlyPickupMessage.value),
      before: beforeDateTime(
        () => combinedModel.value.pickupLatest,
        earlyPickupMessage.value,
        latePickupMessage.value,
        { strict: false },
      ),
    },
    pickupLatest: { serverValidationPlaceholder },
    deliveryEarliest: {
      before: beforeDateTime(
        () => combinedModel.value.deliveryLatest,
        earlyDeliveryMessage.value,
        lateDeliveryMessage.value,
        { strict: false },
      ),
      afterEarliestPickup: afterDateTime(
        () => combinedModel.value.pickupEarliest,
      ),
    },
    deliveryLatest: {
      afterEarliestPickup: afterDateTime(
        () => combinedModel.value.pickupEarliest,
      ),
    },
  };

  if (!isOptionalBrokerageDatesFeatFlagEnabled.value) {
    baseRules.pickupLatest = {
      ...baseRules.pickupLatest,
      required: required(latePickupMessage.value),
    };

    baseRules.deliveryEarliest = {
      ...baseRules.deliveryEarliest,
      required: required(earlyDeliveryMessage.value),
    };

    baseRules.deliveryLatest = {
      ...baseRules.deliveryLatest,
      required: required(lateDeliveryMessage.value),
    };
  }

  return baseRules;
});
const combinedModel = computed(() => {
  return {
    ...internalValue.value,
    ...internalBrokerageDates.value,
  };
});
const { setServerErrors, v$, validate } = useValidations(
  combinedModel,
  rules,
  uid,
);

const clientChargeTableItems: Ref<EditQuoteClientChargeTableItem[]> = ref([]);
const threeplChargeTableItems: Ref<EditQuoteThreeplChargeTableItem[]> = ref([]);
const selectedBrokerSettings: Ref<CarrierSetting | undefined> = ref();
const isAddAdHocBrokeredCarrierModalVisible = ref(false);
const carrierSettings: Ref<Loadable<CarrierSetting>> = ref({
  loading: false,
  data: new CarrierSetting(),
});

const stops = toRef(() => parentEntity.value?.stops ?? []);

const header = computed(() => {
  return internalValue.value.id
    ? t('common.editParam', { param: t('brokerages.brokerages') })
    : t('brokerages.sendToBrokerage');
});

const defaultTenderConfig: Ref<TenderConfig> = computed(() => {
  return (
    selectedBrokerSettings.value?.tenderConfig?.find((t) => t.defaultMethod) ??
    new TenderConfig()
  );
});

// Use computed prop for internalValue.brokerageTenderConfigOverride to
// guarantee it's always defined
const internalTenderConfig: Ref<TenderConfig> = computed({
  get() {
    const override = internalValue.value.brokerageTenderConfigOverride;
    return override?.method !== undefined
      ? override
      : defaultTenderConfig.value;
  },
  set: (newVal: TenderConfig) => {
    internalValue.value.brokerageTenderConfigOverride = new TenderConfig(
      newVal,
    );
  },
});

const chargeTableItems = computed({
  get: () =>
    viewUplift.value
      ? threeplChargeTableItems.value
      : clientChargeTableItems.value,
  set: (newVal: BaseEditQuoteChargeTableItem[]) => {
    if (viewUplift.value) {
      threeplChargeTableItems.value =
        newVal as EditQuoteThreeplChargeTableItem[];
    } else {
      clientChargeTableItems.value = newVal as EditQuoteClientChargeTableItem[];
    }
  },
});

const brokeredCarrier = computed(() => {
  return internalValue.value.brokeredCarrier
    ? Carrier.fromCarrierRef(internalValue.value.brokeredCarrier)
    : null;
});

const isEmailTender = computed(() => {
  return selectedBrokerSettings.value?.tenderConfig?.find(
    (t) => t.method === TenderMethod.EMAIL,
  )?.defaultMethod;
});

const selectedBroker = computed((): Carrier => {
  return selectedBrokerSettings.value?.carrier;
});

const availableModes: Ref<Mode[]> = computed(() => {
  const brokerModes = selectedBroker.value?.modes || [];
  const carrierModes = carrierSettings.value?.data?.modes || [];
  return brokerModes.concat(carrierModes)?.length > 0
    ? dedupeArray(brokerModes.concat(carrierModes))
    : Object.values(Mode);
});

const availableModeOptions: Ref<DropdownOption<Mode>[]> = computed(() => {
  return availableModes.value.map((mode) => ({
    label: t(`enumMap.Mode.${mode}`),
    value: mode,
  }));
});

const availableServiceLevels: Ref<string[]> = computed(() => {
  const brokerServiceLevels =
    selectedBroker.value?.serviceLevels?.map((x) => x.name)?.filter(notNull) ||
    [];
  const carrierServiceLevels =
    carrierSettings.value?.data?.serviceLevels
      ?.map((x) => x.name)
      ?.filter(notNull) || [];
  return brokerServiceLevels.concat(carrierServiceLevels).length > 0
    ? dedupeArray(brokerServiceLevels.concat(carrierServiceLevels))
    : [WellKnownService.STANDARD];
});

const availableEquipmentTypes: Ref<EquipmentType[]> = computed(() => {
  const brokerEquipmentTypes = selectedBroker.value?.equipmentTypes || [];
  const carrierEquipmentTypes =
    carrierSettings.value?.data?.equipmentTypes || [];
  return brokerEquipmentTypes.concat(carrierEquipmentTypes).length > 0
    ? dedupeArray(brokerEquipmentTypes.concat(carrierEquipmentTypes))
    : equipmentTypes.value.data || [];
});

const setDefaultMode = (availableModes: Mode[]) => {
  if (!internalValue.value.mode && availableModes.length === 1) {
    internalValue.value.mode = availableModes[0];
  }
};

const setDefaultServiceLevel = (availableServiceLevels: string[]) => {
  if (!internalValue.value.serviceType && availableServiceLevels.length === 1) {
    internalValue.value.serviceType = availableServiceLevels[0];
  }
};

const verifyMode = () => {
  if (internalValue.value.mode) {
    if (!availableModes.value.find((m) => m === internalValue.value.mode)) {
      internalValue.value.mode = availableModes.value[0];
    }
  }
};

const verifyServiceType = () => {
  if (internalValue.value.serviceType) {
    if (
      !availableServiceLevels.value.find(
        (s) => s === internalValue.value.serviceType,
      )
    ) {
      internalValue.value.serviceType = availableServiceLevels.value[0];
    }
  }
};

const verifyEquipmentType = () => {
  if (internalValue.value.equipmentType) {
    if (
      !availableEquipmentTypes.value.find(
        (e) => e.id === internalValue.value.equipmentType?.id,
      )
    ) {
      internalValue.value.equipmentType = availableEquipmentTypes.value[0];
    }
  }
};

const verifyDropdowns = () => {
  verifyMode();
  verifyServiceType();
  verifyEquipmentType();
};

const loadCarrierSettings = async (carrier: Carrier) => {
  if (!carrier.scac || !authorization.hasAuthority('client', 'read')) {
    return;
  }

  try {
    carrierSettings.value.loading = true;
    carrierSettings.value.data =
      carrier.type === CapacityProviderType.CARRIER
        ? await api.carrier.findCarrierSettingsForScac(carrier.scac)
        : await api.broker.findBrokerSettingsForScac(carrier.scac);
    verifyDropdowns();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No CarrierSettings found but no need to show an error
      carrierSettings.value.data = new CarrierSetting();
      return;
    }
    console.error("Error loading selected carrier's settings", e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.carrierSettings', 2),
      }),
      error: e,
    });
  } finally {
    carrierSettings.value.loading = false;
  }
};

// Two `immediate` watches and the dropdown all trigger this, so a slow response
// for an earlier broker must not land on top of a newer selection.
let requestedBrokerScac: string | undefined;
const loadSelectedBrokerSettings = async (scac?: string) => {
  if (selectedBrokerSettings.value?.carrier?.scac === scac) {
    return;
  }

  requestedBrokerScac = scac;

  if (!scac || !authorization.hasAuthority('client', 'read')) {
    selectedBrokerSettings.value = undefined;
    return;
  }

  try {
    const setting = await api.broker.findBrokerSettingsForScac(scac);

    if (requestedBrokerScac === scac) {
      selectedBrokerSettings.value = setting;
    }
  } catch (e) {
    if (axios.isCancel(e) || requestedBrokerScac !== scac) {
      return;
    }

    selectedBrokerSettings.value = undefined;

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // A quote whose broker is no longer configured for the client is a normal
      // miss, so the dropdown just stays empty.
      return;
    }

    console.error("Error loading selected broker's settings", e);
  }
};

const onBrokerSelected = (broker?: Carrier) => {
  internalValue.value.carrier = broker;
  loadSelectedBrokerSettings(broker?.scac);
};

const onAdHocBrokeredCarrierAdded = (carrierRef: CarrierRef): void => {
  internalValue.value.brokeredCarrier = new CarrierRef(carrierRef);
};

const showAddAdHocBrokeredCarrierModal = (): void => {
  isAddAdHocBrokeredCarrierModalVisible.value = true;
};

const populateAccessorials = () => {
  const topLevelAccessorials =
    parentEntity.value?.requestedAccessorials.map(
      (code) => new RateQuoteAccessorial({ code }),
    ) ?? [];
  const stopLevelAccessorials = stops.value
    .map((s) => {
      const stopSequenceNumber = s.sequenceNumber;
      return s.requestedAccessorials.map(
        (code) => new RateQuoteAccessorial({ code, stopSequenceNumber }),
      );
    })
    .flat();

  internalValue.value.accessorials = [
    ...topLevelAccessorials,
    ...stopLevelAccessorials,
  ];

  if (!internalValue.value.id) {
    return;
  }

  internalValue.value.accessorials.forEach((a) => {
    const rateCharge = internalValue.value.rawCharges.find(
      (c) =>
        c.code === a.code?.naturalId &&
        c.stopSequenceNumber === a.stopSequenceNumber,
    );
    a.charge = rateCharge?.charge;
  });
};

const initializeEditQuoteChargeTableItems = () => {
  if (viewUplift.value) {
    threeplChargeTableItems.value = toThreeplChargeTableItems(
      internalValue.value,
    );
  } else {
    clientChargeTableItems.value = toClientChargeTableItems(
      internalValue.value,
    );
  }
};

/**
 * Accessorials keep the description they came with (a new one has none), as
 * `BrokerageModal` did, rather than their code's name.
 */
const updateRateQuoteTableValues = () => {
  const original = internalValue.value;
  const updated = viewUplift.value
    ? fromThreeplChargeTableItems(original, threeplChargeTableItems.value)
    : fromClientChargeTableItems(original, clientChargeTableItems.value);

  internalValue.value = new RateQuote({
    ...updated,
    accessorials: updated.accessorials.map(
      (accessorial) =>
        new RateQuoteAccessorial({
          ...accessorial,
          accessorialDescription: original.accessorials.find(
            (o) => o.id !== undefined && o.id === accessorial.id,
          )?.accessorialDescription,
        }),
    ),
  });
};

const onNext = async () => {
  updateRateQuoteTableValues();

  if (await validate()) {
    // Set undefined fuel/linehaul to 0 to pass database validation
    if (!internalValue.value.rawLineHaulGrossCharge) {
      internalValue.value.rawLineHaulGrossCharge = 0;
    }
    if (!internalValue.value.fuelSurchargeAmount) {
      internalValue.value.fuelSurchargeAmount = 0;
    }
    internalValue.value.brokerageTenderConfigOverride =
      internalTenderConfig.value;

    isVisible.value = false;
    emit('confirm', {
      brokerageQuote: internalValue.value,
      brokerageDates: internalBrokerageDates.value,
    });
  }
};

watch(
  () => brokerageInfo.value.data,
  (newVal: RateQuote | undefined) => {
    internalValue.value = new RateQuote(newVal);
    v$.value.$reset();

    if (internalValue.value.carrier) {
      loadSelectedBrokerSettings(internalValue.value.carrier.scac);
    }

    nextTick(() => {
      setDefaultMode(availableModes.value);
      setDefaultServiceLevel(availableServiceLevels.value);
    });
  },
  { deep: true, immediate: true },
);

watch(
  () => brokerageInfo.value.error,
  (newVal: Optional<ErrorResponse>) => {
    setServerErrors(newVal);
  },
);

watch(
  selectedBrokerSettings,
  (newVal?: CarrierSetting, oldVal?: CarrierSetting) => {
    if (newVal) {
      internalValue.value.carrier = newVal.carrier;

      const isSwitchingClientCarrier =
        !!oldVal && newVal.carrier?.scac !== oldVal.carrier?.scac;
      const isEmptyConfigOptions =
        internalTenderConfig.value.configOptions?.isEmpty() ?? true;
      if (isSwitchingClientCarrier || isEmptyConfigOptions) {
        const newDefaultTenderConfig =
          newVal.tenderConfig?.find((t) => t.defaultMethod) ??
          new TenderConfig();
        internalTenderConfig.value = new TenderConfig(newDefaultTenderConfig);
      }
    }
  },
);

watch(
  () => brokerageDates?.value,
  (newVal?: BrokerageDates) => {
    internalBrokerageDates.value = new BrokerageDates(newVal);
    nextTick(() => v$.value.$reset());
  },
  { deep: true, immediate: true },
);

watch(
  isVisible,
  (newVal) => {
    if (newVal && !brokerageInfo.value.error?.errorDetails?.fieldErrors) {
      nextTick(() => {
        const el = firstInput.value?.$el as HTMLElement | null;
        const input = el?.querySelector(
          'input,select,textarea',
        ) as HTMLElement | null;
        input?.focus();
      });

      internalValue.value = new RateQuote(brokerageInfo.value.data);
      internalBrokerageDates.value = new BrokerageDates(brokerageDates?.value);
      loadSelectedBrokerSettings(internalValue.value.carrier?.scac);

      if (!internalValue.value.id) {
        populateAccessorials();
      }

      loadRequestableAccessorialCodes();
      loadEquipmentTypes();
      initializeEditQuoteChargeTableItems();

      if (internalValue.value.carrier) {
        loadCarrierSettings(internalValue.value.carrier);
      }

      nextTick(() => v$.value.$reset());
    }
  },
  { immediate: true },
);

watch(availableModes, (newVal: Mode[]) => {
  setDefaultMode(newVal);
});

watch(availableServiceLevels, (newVal: string[]) => {
  setDefaultServiceLevel(newVal);
});

/**
 * Handles if modal is opened pre-maturely and chargeTable initializes with `viewUplift` as false (incorrectly).
 * Shouldn't happen with the `disabled` guard on the `+ Add Quote` primary action, but this component shouldn't
 * have to rely on that
 */
watch(viewUplift, () => {
  if (isVisible.value) {
    initializeEditQuoteChargeTableItems();
  }
});

onMounted(() => {
  loadEquipmentTypes();
});
</script>

<style>
.edit-brokerage-quote-modal {
  .edit-quote-modal-carrier-profile {
    margin-bottom: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: var(--divider);

    .carrier-icon {
      margin-bottom: var(--sp-3);
    }

    .carrier-name {
      margin-bottom: var(--sp-6);
    }

    .carrier-info-row {
      display: flex;
      gap: var(--sp-3);
      justify-content: center;
    }
  }

  .p-message {
    margin: 0;
  }

  .edit-quote-modal-form {
    overflow: auto;
    scroll-padding: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: var(--divider);

    .edit-quote-modal-form-row {
      display: flex;
      gap: var(--sp-3);

      &.accessorial-row {
        margin-top: var(--sp-3);

        .rate-quote-accessorials {
          margin-top: var(--sp-1);
        }
      }
    }
  }
}
</style>
